import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { OPEN_API } from "../services/api";

export default function RegisterForm() {
  const navigate = useNavigate();

  // ================================
  //   Estado inicial único
  // ================================
  const initialFormData = useMemo(
    () => ({
      nombre: "",
      apellidos: "",
      celular: "",
      correo: "",
      contrasena: "",
      empresa: "",
      cargo: "",
      area: "",
      agente: "",
      actividad: "", // id
      actividadDesc: "", // descripción
    }),
    []
  );

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "transparent",
      border: "none",
      borderBottom: "2px solid #6b7280", // gray-500
      boxShadow: "none",
      borderRadius: 0,
      minHeight: "42px",
      color: "#ffffff",
      cursor: "pointer",
      ":hover": {
        borderBottom: "2px solid #00e68a",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: 0,
      paddingRight: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af", // gray-400
    }),
    singleValue: (base) => ({
      ...base,
      color: "#ffffff",
    }),
    input: (base) => ({
      ...base,
      color: "#ffffff",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#ffffff",
      color: "#111827",
      zIndex: 50,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#00e68a"
        : state.isFocused
        ? "#e5e7eb"
        : "#ffffff",
      color: state.isSelected ? "#1f1b24" : "#111827",
      cursor: "pointer",
    }),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [actividadError, setActividadError] = useState("");

  const [actividadesEconomicas, setActividadesEconomicas] = useState([]);
  const [actividades, setActividades] = useState([]);

  // ================================
  //  Helper para consumir QuarkIA GET
  // ================================
  const quarkiaGet = async (tipo_servicio) => {
    const payload = {
      datos: {},
      generales: {
        tipo_servicio,
        operacion: "traertodos",
        operacion_tipo: "consulta",
      },
    };

    const res = await OPEN_API.get("/api/open/quarkia", {
      params: { body: JSON.stringify(payload) },
    });

    return Array.isArray(res?.data?.data) ? res.data.data : [];
  };
  // ================================
  //  Cargar combos (economicas + actividades)
  // ================================
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [economicas, acts] = await Promise.all([
          quarkiaGet("qahe-actividadeconomica"),
          quarkiaGet("qahe-actividad"),
        ]);

        setActividadesEconomicas(economicas);
        setActividades(acts);
      } catch (err) {
        console.error(err);
        setActividadesEconomicas([]);
        setActividades([]);
      }
    };

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================================
  //   Opciones React-Select memoizadas
  // ================================
  const opcionesAgenteSi = useMemo(
    () =>
      actividades.map((item) => ({
        value: item.id,
        label: item.descripcion,
      })),
    [actividades]
  );

  const opcionesAgenteNo = useMemo(
    () =>
      actividadesEconomicas.map((item) => ({
        value: item.id,
        label: item.descripcion,
      })),
    [actividadesEconomicas]
  );

  const opcionesActividad = formData.agente === "si" ? opcionesAgenteSi : opcionesAgenteNo;

  // ================================
  //       Manejar cambios inputs
  // ================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Si cambia agente: reset actividad
    if (name === "agente") {
      setFormData((prev) => ({
        ...prev,
        agente: value,
        actividad: "",
        actividadDesc: "",
      }));
      setActividadError("");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ================================
  //      Manejar cambio React-Select
  // ================================
  const handleActividadSelect = (selected) => {
    setFormData((prev) => ({
      ...prev,
      actividad: selected?.value ?? "",
      actividadDesc: selected?.label ?? "",
    }));
    setActividadError("");
  };

  // ================================
  //        Enviar formulario
  // ================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.actividad) {
      setActividadError("Selecciona una actividad.");
      return;
    }
    setActividadError("");

    try {
      const datosEnviar = {
        request: "post",
        key_original: String(Date.now()),
        datos: {
          nombre: formData.nombre,
          apellidos: formData.apellidos,
          celular: Number(formData.celular),
          correo: formData.correo,
          contrasena: formData.contrasena,
          empresa: formData.empresa,
          // opcionales: si vienen vacíos, manda "."
          cargo: formData.cargo?.trim() ? formData.cargo.trim() : ".",
          area: formData.area?.trim() ? formData.area.trim() : ".",
          agente: formData.agente,
          actividad: Number(formData.actividad),
          actividad_desc: formData.actividadDesc,
        },
        generales: {
          tipo_servicio: "qahe-registro",
          operacion: "agregar",
          operacion_tipo: "post",
        },
      };

      const res = await OPEN_API.post("/api/open/quarkia", datosEnviar, {
        headers: { "Content-Type": "application/json"},
      });
      const data = res?.data ?? {};

      if (data?.errores) {
        const errores = data.errores || {};
        if (errores.actividad) setActividadError(errores.actividad);
        return;
      }

      setFormData(initialFormData);
      setActividadError("");
      navigate("/login", { replace: true });
    } catch (err) {
      const backend = err?.response?.data;
      const errores = backend?.errores || backend || {};
      if (errores?.actividad) setActividadError(errores.actividad);
      console.error("Error registro:", backend || err);
    }
  };

  // ================================
  //   Estilos React-Select (menú zIndex)
  // ================================
  const selectCommonProps = {
    className: "w-full",
    menuPlacement: "auto",
    maxMenuHeight: 220,
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    styles: {
      ...selectStyles, // ✅ usa tus estilos (texto blanco, placeholder gris, etc.)
      menuPortal: (base) => ({ ...base, zIndex: 9999 }), // ✅ y además portal arriba de todo
    },
  };

  const selectedValue =
    formData.actividad && formData.actividadDesc
      ? { value: formData.actividad, label: formData.actividadDesc }
      : null;

  const showActividadSelect = formData.agente === "si" || formData.agente === "no";

  return (
    <div className="min-h-screen bg-[#1f1b24] text-white font-nexa font-normal">
      <Navbar />

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 space-y-16">
        {/* Título */}
        <div className="flex justify-start mb-12">
          <div
            className="inline-block text-[#1f1b24] font-extrabold text-2xl md:text-3xl px-8 py-4 rounded-2xl shadow-lg"
            style={{ background: "linear-gradient(90deg, #00b3b3, #00e68a)" }}
          >
            Registro QuarkIA
          </div>
        </div>

        {/* Sección gratuita */}
        <div>
          <h2 className="text-2xl font-semibold text-[#00e68a] mb-4">
            Acceso a información gratuita
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Regístrate para acceder a los tableros de datos gratuitos.
          </p>

          <form
            onSubmit={handleSubmit}
            className="bg-[#2c2833] p-8 rounded-2xl shadow-lg space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                type="text"
                name="nombre"
                placeholder="Nombre"
                value={formData.nombre}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
                required
              />

              <input
                type="text"
                name="apellidos"
                placeholder="Apellidos"
                value={formData.apellidos}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
                required
              />

              <input
                type="text"
                name="celular"
                placeholder="Celular"
                value={formData.celular}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
                required
              />

              <input
                type="email"
                name="correo"
                placeholder="Correo electrónico"
                value={formData.correo}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
                required
              />

              <input
                type="password"
                name="contrasena"
                placeholder="Contraseña"
                value={formData.contrasena}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
                required
              />

              <input
                type="text"
                name="empresa"
                placeholder="Empresa"
                value={formData.empresa}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
                required
              />

              <input
                type="text"
                name="cargo"
                placeholder="Cargo (opcional)"
                value={formData.cargo}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
              />

              <input
                type="text"
                name="area"
                placeholder="Área (opcional)"
                value={formData.area}
                onChange={handleChange}
                className="bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2"
              />

              {/* Agente + Actividad */}
              <div className="flex flex-col md:flex-row gap-4 md:col-span-2 items-center">
                <select
                  name="agente"
                  value={formData.agente}
                  onChange={handleChange}
                  className="flex-1 bg-transparent border-b-2 border-gray-500 focus:border-[#00e68a] outline-none py-2 text-gray-300"
                  required
                >
                  <option value="">¿Eres agente del sector?</option>
                  <option value="si" className="text-black">
                    Sí
                  </option>
                  <option value="no" className="text-black">
                    No
                  </option>
                </select>

                <div className="w-full">
                  {showActividadSelect && (
                    <Select
                      {...selectCommonProps}
                      value={selectedValue}
                      options={opcionesActividad}
                      onChange={handleActividadSelect}
                      placeholder={
                        formData.agente === "si"
                          ? "Selecciona tu actividad"
                          : "Selecciona tu actividad económica"
                      }
                    />
                  )}

                  {actividadError && (
                    <p className="text-red-500 text-sm mt-1">{actividadError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Botón */}
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-gradient-to-r from-[#00b3b3] to-[#00e68a] text-[#1f1b24] font-semibold px-8 py-3 rounded-xl mt-6 hover:scale-105 transition-transform"
              >
                Registrarse
              </button>
            </div>
          </form>
        </div>

        {/* Sección premium */}
        <div>
          <h2 className="text-2xl font-semibold text-[#00e68a] mb-4">Acceso premium</h2>
          <p className="text-lg text-gray-300 mb-8">
            Datos históricos, tiempo real y modelos predictivos.
          </p>

          <div className="bg-[#2c2833] p-8 rounded-2xl shadow-lg flex flex-col md:flex-row items-center justify-between">
            <span className="text-gray-300 mb-4 md:mb-0">
              Da el siguiente paso hacia la inteligencia energética.
            </span>
            <button
              onClick={() => navigate("/login")}
              className="bg-gradient-to-r from-[#00b3b3] to-[#00e68a] text-[#1f1b24] font-semibold px-8 py-3 rounded-xl hover:scale-105 transition-transform"
            >
              Suscríbete aquí
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
